import { BackendRoute } from '../config/index';
import { HttpException } from '../exceptions/index';
import { accessTokenStorage } from '../storage/access-token-storage.util';
import { HttpService } from './http-service.util';
import { QueryParams, QueryParamsBuilder } from './query-param-builder.util';

export class ApiService extends HttpService {
  private queryParamBuilder = new QueryParamsBuilder();
  private basicRoutePath = '/admin-panel';
  constructor(public readonly backendUrl = process.env.REACT_APP_BACKEND_URL) {
    super();
  }

  public buildRoutePath(routePath: (number | string)[] = []) {
    return routePath.length > 0 ? '/' + routePath.join('/') : '';
  }

  public async delete<T, B>(
    { endpoint, routePath = [] }: { endpoint: BackendRoute; routePath?: (number | string)[] },
    {
      body,
      headers,
      ...params
    }: {
      body?: B;
      headers?: Record<string, string>;
      queryParams?: QueryParams;
      regularBackend?: boolean;
    } = {},
  ) {
    return this.requestWithAuth<T>(endpoint + this.buildRoutePath(routePath), {
      init: {
        method: 'DELETE',
        body: JSON.stringify(body),
        headers,
      },
      ...params,
    });
  }

  // public async downloadFile<T>(
  //   {
  //     endpoint,
  //     routePath = [],
  //   }: {
  //     endpoint: BackendRoute;
  //     routePath?: (string | number)[];
  //   },

  //   { queryParams, body }: { queryParams?: QueryParams,   regularBackend?: boolean;; body?: T } = {},
  // ) {
  //   try {
  //     const res = await fetch(
  //       super.buildQueryParam(
  //         this.getJoinedPath(endpoint + this.buildRoutePath(routePath)),
  //         queryParams,
  //       ),
  //       {
  //         method: body ? 'POST' : 'GET',
  //         body: JSON.stringify(body),
  //         headers: {
  //           credentials: 'include',
  //           'Content-Type': 'application/json',
  //           Authorization: `Bearer ${accessTokenStorage.get() || ''}`,
  //         },
  //       },
  //     );

  //     const filename = decodeURIComponent(
  //       res?.headers?.get('Content-Disposition')?.split('filename=')[1] || '',
  //     );
  //     const blob = await res.blob();

  //     const file = window.URL.createObjectURL(blob);
  //     const a = document.createElement('a');

  //     a.href = file;
  //     a.download = filename;

  //     document.body.appendChild(a);

  //     a.click();
  //     a.remove();

  //     URL.revokeObjectURL(file);
  //   } catch (e) {
  //     try {
  //       await this.refreshTokens();
  //     } catch (_ingore) {
  //       throw e;
  //     }
  //   }
  // }

  public async get<T>(
    {
      endpoint,
      routePath = [],
    }: {
      endpoint: BackendRoute;
      routePath?: (number | string)[];
    },
    params: { queryParams?: QueryParams; regularBackend?: boolean } = {},
  ) {
    return this.requestWithAuth<T>(endpoint + this.buildRoutePath(routePath), {
      ...params,
    });
  }

  public getJoinedPath(endpoint: string, regularBackend?: boolean) {
    return `${this.backendUrl}${regularBackend ? '' : this.basicRoutePath}${endpoint}`;
  }

  public async patch<T, B>(
    { endpoint, routePath = [] }: { endpoint: BackendRoute; routePath?: (number | string)[] },
    {
      body,
      headers,
      ...params
    }: {
      body?: B;
      headers?: Record<string, string>;
      queryParams?: QueryParams;
      regularBackend?: boolean;
    } = {},
  ) {
    return this.requestWithAuth<T>(endpoint + this.buildRoutePath(routePath), {
      init: {
        method: 'PATCH',
        body: JSON.stringify(body),
        headers,
      },
      ...params,
    });
  }

  public async post<T, B = unknown>(
    { endpoint, routePath = [] }: { endpoint: BackendRoute; routePath?: (number | string)[] },
    {
      body,
      headers,
      ...params
    }: {
      body?: B;
      headers?: Record<string, string>;
      queryParams?: QueryParams;
      regularBackend?: boolean;
    } = {},
  ) {
    return this.requestWithAuth<T>(endpoint + this.buildRoutePath(routePath), {
      init: {
        method: 'POST',
        body: JSON.stringify(body),
        headers,
      },
      ...params,
    });
  }

  private refreshLocked = false;
  private refreshLockCallbacks: ((error?: Error) => void)[] = [];

  public notifyRefreshLock(error?: Error) {
    const callbacks = [...this.refreshLockCallbacks];
    this.refreshLockCallbacks = [];
    callbacks.forEach((callback) => callback(error));
  }

  public async refreshTokens() {
    if (this.refreshLocked) {
      return new Promise<void>((resolve, reject) => {
        this.refreshLockCallbacks.push((error?: Error) => {
          if (error) {
            reject(error);
          } else {
            resolve(undefined);
          }
        });
      });
    }
    this.refreshLocked = true;

    let refreshError: Error | undefined;
    try {
      const { accessToken } = await super.requestWithAuth<{
        accessToken: string;
      }>(this.getJoinedPath(BackendRoute.AUTH_REFRESH));

      accessTokenStorage.set(accessToken);
    } catch (error) {
      refreshError = error instanceof Error ? error : new Error(String(error));
      throw error;
    } finally {
      this.refreshLocked = false;
      this.notifyRefreshLock(refreshError);
    }
  }

  public uploadFile<TResponse = Response>({ regularBackend }: { regularBackend?: boolean } = {}) {
    const xhr = new XMLHttpRequest();

    const getCallback = async (
      { endpoint, routePath = [] }: { endpoint: BackendRoute; routePath?: (number | string)[] },
      [key, file]: [string, File],
      params?: {
        onAboard?: () => void;
        onUploadProgress?: (progress: number) => void;
        queryParams?: QueryParams;
        requestMethod?: 'PATCH' | 'POST' | 'PUT';
      },
    ) => {
      const accessToken = accessTokenStorage.get();

      const reqMethod = params?.requestMethod || 'POST';
      const onUploadProgress = params?.onUploadProgress;
      const onAboard = params?.onAboard;
      const queryParams = params?.queryParams;

      const fileData = new FormData();
      fileData.append(key, file);
      const promiseCallback = new Promise<TResponse>((resolve, reject) => {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && onUploadProgress) {
            onUploadProgress((event.loaded / event.total) * 100);
          }
        });
        xhr.onabort = () => onAboard?.();

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.response);
          } else {
            reject(
              new HttpException(xhr.response, JSON.parse(xhr.response) as { message?: string }),
            );
          }
        };

        const queries = queryParams ? this.queryParamBuilder.buildQuery(queryParams) : '';

        xhr.open(
          reqMethod,
          this.getJoinedPath(endpoint + this.buildRoutePath(routePath) + queries, regularBackend),
          true,
        );

        xhr.withCredentials = true;
        xhr.setRequestHeader('Authorization', 'Bearer ' + accessToken);
        xhr.send(fileData);
      });

      return promiseCallback;
    };

    return {
      abort: () => xhr.abort(),
      callback: getCallback,
    };
  }

  public async requestWithAuth<T>(
    route: string,
    {
      init,
      regularBackend,
      ...params
    }: {
      init?: RequestInit | undefined;
      queryParams?: QueryParams;
      regularBackend?: boolean;
    } = {},
  ): Promise<T> {
    const accessToken = accessTokenStorage.get() || '';

    const url = this.getJoinedPath(route, regularBackend);

    let res: T;

    try {
      res = await super.requestWithAuth<T>(url, {
        init: {
          ...init,
          headers: { 'Content-Type': 'application/json', ...init?.headers },
        },
        ...params,
        accessToken,
      });
      return res;
    } catch (e) {
      if (!(e instanceof HttpException) || e.response?.status !== 401) throw e;

      try {
        await this.refreshTokens();
      } catch (_ingore) {
        throw e;
      }
      res = await super.requestWithAuth<T>(url, {
        init: {
          ...init,
          headers: { 'Content-Type': 'application/json', ...init?.headers },
        },
        ...params,
        accessToken: accessTokenStorage.get() || '',
      });
      return res;
    }
  }
}

const apiService = new ApiService();

export default apiService;
